import React, { useState, useEffect, useMemo, useCallback } from "react";
/* eslint-disable react/prop-types */
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
    ShieldCheckIcon,
    CheckCircleIcon,
    ClockIcon,
    ChartBarIcon,
    FunnelIcon,
    UserIcon,
    BuildingLibraryIcon,
    CalendarIcon,
    XCircleIcon,
    EyeIcon,
    CloudArrowUpIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import UploadFileModal from "../../ui/UploadFileModal";
import { useAuth } from "../../../context/AuthContext";
import { useSnackbar } from "../../../context/SnackbarContext";
import {
  getBgvList,
  getBgvAgencies,
  saveBgvRecord,
  uploadEmployeeFile,
} from "../../../services/axios/AuthApis/TeamCollabApis";
import ApiLoader from "../../ui/ApiLoader";
import { DataTable } from "../../common/DataTable";

const initialBgvFilters = {
  employee: "",
  department: "",
  doj: "",
};

const initialBgvForm = {
  employee_id: "",
  employee_name: "",
  department: "",
  role: "",
  bgv_status: "pending",
  doj: "",
  bgv_agency: "",
  verification_type: "standard",
  documents_verified: [],
  remarks: "",
};

const BGVTab = ({ dashboardData, customerMasterId }) => {
  const { user } = useAuth();
  const authToken = localStorage.getItem("token") || user?.token;
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const [bgvRecords, setBgvRecords] = useState([]);
  const [bgvFilters, setBgvFilters] = useState(initialBgvFilters);
  const [bgvPagination, setBgvPagination] = useState({
    totalPages: 1,
    totalCount: 0,
    currentPage: 1,
    pageSize: 20,
  });
  const [showBgvModal, setShowBgvModal] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [bgvForm, setBgvForm] = useState(initialBgvForm);
  const [agencies, setAgencies] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const hasTeamCollabAccess =
    user?.employee_details?.module_access_to?.["team-collaboration"] === true;

  const getBgvStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "bg-green-100 text-green-800";
      case "Inactive":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const resetBgvForm = () => {
    setBgvForm(initialBgvForm);
  };

  const fetchAgencies = async () => {
    try {
      const data = await getBgvAgencies();
      setAgencies(data.agencies || []);
    } catch (err) {
      console.error("Error fetching agencies:", err);
    }
  };

  const loadBgvList = useCallback(
    async (page = 1, pageSize = 20) => {
      try {
        setLoading(true);
        let userId = user?.id;
        let customerId = customerMasterId;

        if (user?.role === "security_analyst" && selectedCompany) {
          userId = selectedCompany.user_id;
          customerId = selectedCompany.id;
        } else if (user?.role === "end_user" && hasTeamCollabAccess) {
          userId = user?.customer_master_id;
        }

        if (!userId && !customerId) {
          console.warn("BGVTab: missing user_id and customer_master_id, skipping load");
          return;
        }

        const res = await getBgvList({
          user_id: userId,
          customer_master_id: customerId,
          page,
          page_size: pageSize,
          token: authToken,
        });

        setBgvRecords(res.results || []);
        setBgvPagination({
          totalPages: res.total_pages || 1,
          totalCount: res.count || 0,
          currentPage: res.current_page || page,
          pageSize: res.page_size || pageSize,
        });
      } catch (err) {
        console.error("Error fetching BGV list:", err);
      } finally {
        setLoading(false);
      }
    },
    [authToken, user, selectedCompany, customerMasterId, hasTeamCollabAccess],
  );

  useEffect(() => {
    loadBgvList(1, bgvPagination.pageSize);
  }, [loadBgvList]);

  const handleSaveBgvRecord = async () => {
    const token = authToken;
    if (!token) {
      showSnackbar("Authentication token missing", "error");
      return;
    }

    const payload = {
      ...bgvForm,
      verification_type: bgvForm.verification_type,
      bgv_status: bgvForm.bgv_status,
      bgv_agency: bgvForm.bgv_agency,
    };

    try {
      await saveBgvRecord({
        modalMode,
        employeeId: bgvForm.employee_id,
        payload,
        token,
      });

      showSnackbar(
        `BGV record ${modalMode === "create" ? "created" : "updated"} successfully`,
        "success",
      );
      setShowBgvModal(false);
      resetBgvForm();
      loadBgvList(1, bgvPagination.pageSize);
    } catch (error) {
      console.error("Error saving BGV record:", error);
      showSnackbar("Failed to save BGV record", "error");
    }
  };

  const handleUploadFile = async () => {
    if (!selectedFile) {
      showSnackbar("Please select a file", "error");
      return;
    }

    if (!selectedRecord?.employee_id) {
      showSnackbar("Please select a record", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("description", fileDescription);

    try {
      await uploadEmployeeFile({
        employeeId: selectedRecord.employee_id,
        formData,
        userId: user.id,
      });

      showSnackbar("File uploaded successfully!", "success");
      setSelectedFile(null);
      setFileDescription("");
      setShowUploadFile(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      showSnackbar("Unexpected error", "error");
    }
  };

  const filteredBgvRecords = useMemo(() => {
    let list = [...bgvRecords];

    if (bgvFilters.employee) {
      list = list.filter((record) =>
        String(record.firstname || "")
          .toLowerCase()
          .includes(bgvFilters.employee.toLowerCase()),
      );
    }

    if (bgvFilters.department) {
      list = list.filter((record) =>
        String(record.department || "")
          .toLowerCase()
          .includes(bgvFilters.department.toLowerCase()),
      );
    }

    if (bgvFilters.doj) {
      list = list.filter((record) => {
        if (!record.date_of_joining) return false;
        return String(record.date_of_joining).startsWith(bgvFilters.doj);
      });
    }

    return list;
  }, [bgvRecords, bgvFilters]);

  const hasActiveBgvFilters =
    !!bgvFilters.employee || !!bgvFilters.department || !!bgvFilters.doj;

  const bgvColumns = [
    {
      id: "employee",
      header: "Employee",
      cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.firstname}</span>,
    },
    {
      accessorKey: "department",
      header: "Department",
      cell: ({ getValue }) => <span className="text-gray-900">{getValue() || "—"}</span>,
    },
    {
      id: "doj",
      header: "DOJ",
      cell: ({ row }) => (
        <span className="text-gray-900">
          {row.original.date_of_joining ? new Date(row.original.date_of_joining).toLocaleDateString("en-GB") : "—"}
        </span>
      ),
    },
    {
      id: "status",
      header: "BGV Status",
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full font-medium ${getBgvStatusColor(row.original.bgv_status)}`}>
          {row.original.verification_status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      accessorKey: "agency_name",
      header: "Agency",
      cell: ({ getValue }) => <span className="text-gray-900">{getValue() || "Not Assigned"}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const record = row.original;
        return (
          <div className="flex items-left justify-left space-x-2">
            <button
              onClick={() => {
                setSelectedRecord(record);
                setBgvForm(record);
                setModalMode("edit");
                navigate("/documents", { state: { openTab: "bgv", selectedRecord: record } });
              }}
              className="text-blue-600 hover:text-blue-900"
            >
              <EyeIcon className="w-4 h-4 mr-1" />
            </button>
            {(user?.role === "customer_admin" || user?.role === "security_analyst") && (
              <button
                onClick={() => {
                  setSelectedRecord(record);
                  setShowUploadFile(true);
                }}
                className="text-blue-600 hover:text-blue-900"
                title="Upload verification document"
              >
                <CloudArrowUpIcon className="w-4 h-4 mr-1" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <ShieldCheckIcon className="w-8 h-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Employees</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.bgv_summary?.total_employees || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Verified</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.bgv_summary?.verified || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <ClockIcon className="w-8 h-8 text-yellow-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.bgv_summary?.pending || 0}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <ChartBarIcon className="w-8 h-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Verification Rate</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardData.bgv_summary?.verification_rate || 0}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mt-1">
            <FunnelIcon className="w-5 h-5 text-blue-600" />
          </div>

          <div>
            <h3 className="text-lg text-left font-semibold text-gray-900">Filters</h3>
            <p className="text-xs text-gray-500 mt-1">
              {loading ? (
                <span className="text-blue-600">Loading records…</span>
              ) : (
                <span>
                  Showing <span className="font-semibold text-blue-600">{filteredBgvRecords.length}</span> record
                  {filteredBgvRecords.length !== 1 ? "s" : ""}
                  {hasActiveBgvFilters && " with active filters"}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
              <UserIcon className="w-4 h-4 mr-1.5 text-blue-600" />
              Employee
            </label>
            <input
              type="text"
              value={bgvFilters.employee}
              onChange={(e) => setBgvFilters((prev) => ({ ...prev, employee: e.target.value }))}
              placeholder="Search employee"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
              <BuildingLibraryIcon className="w-4 h-4 mr-1.5 text-indigo-600" />
              Department
            </label>
            <input
              type="text"
              value={bgvFilters.department}
              onChange={(e) => setBgvFilters((prev) => ({ ...prev, department: e.target.value }))}
              placeholder="Search department"
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
            />
          </div>

          <div className="space-y-2">
            <label className="flex items-center text-xs font-medium text-gray-700 uppercase tracking-wider">
              <CalendarIcon className="w-4 h-4 mr-1.5 text-purple-600" />
              DOJ
            </label>
            <input
              type="date"
              value={bgvFilters.doj}
              onChange={(e) => setBgvFilters((prev) => ({ ...prev, doj: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:border-gray-400"
            />
          </div>

          <div className="space-y-2 flex items-end">
            <button
              type="button"
              onClick={() => setBgvFilters({ employee: "", department: "", doj: "" })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm bg-gray-50 hover:bg-gray-100 transition-all duration-200"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {hasActiveBgvFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center flex-wrap gap-2">
              <span className="text-xs font-medium text-gray-600">Active Filters:</span>

              {bgvFilters.employee && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Employee: {bgvFilters.employee}
                  <button
                    type="button"
                    onClick={() => setBgvFilters((prev) => ({ ...prev, employee: "" }))}
                    className="ml-1.5 hover:text-blue-900"
                  >
                    <XCircleIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}

              {bgvFilters.department && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  Department: {bgvFilters.department}
                  <button
                    type="button"
                    onClick={() => setBgvFilters((prev) => ({ ...prev, department: "" }))}
                    className="ml-1.5 hover:text-indigo-900"
                  >
                    <XCircleIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}

              {bgvFilters.doj && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  DOJ: {bgvFilters.doj}
                  <button
                    type="button"
                    onClick={() => setBgvFilters((prev) => ({ ...prev, doj: "" }))}
                    className="ml-1.5 hover:text-purple-900"
                  >
                    <XCircleIcon className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Background Verification Records</h3>
          <p className="text-sm text-gray-600 mt-1">Track employee background verification status and DOJ</p>
        </div>
        <DataTable
          columns={bgvColumns}
          data={filteredBgvRecords}
          getRowId={(record) => String(record.employee_id)}
          loading={loading}
          emptyMessage="No assets found"
          manualPagination
          rowCount={bgvPagination.totalCount || 0}
          pagination={{ pageIndex: (bgvPagination.currentPage || 1) - 1, pageSize: bgvPagination.pageSize }}
          onPaginationChange={(updater) => {
            const current = { pageIndex: (bgvPagination.currentPage || 1) - 1, pageSize: bgvPagination.pageSize };
            const next = typeof updater === "function" ? updater(current) : updater;
            loadBgvList(next.pageIndex + 1, next.pageSize);
          }}
          enableColumnVisibility={false}
          enableColumnResizing
          maxBodyHeight="65vh"
        />
      </div>

      <AnimatePresence>
        {showBgvModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {modalMode === "create" ? "Add BGV Record" : "Edit BGV Record"}
                  </h3>
                  <button
                    onClick={() => setShowBgvModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {modalMode === "create" ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Employee Name</label>
                      <input
                        type="text"
                        value={bgvForm.employee_name}
                        onChange={(e) =>
                          setBgvForm({
                            ...bgvForm,
                            employee_name: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                      <input
                        type="text"
                        value={bgvForm.department}
                        onChange={(e) =>
                          setBgvForm({
                            ...bgvForm,
                            department: e.target.value,
                          })
                        }
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                      <input
                        type="text"
                        value={bgvForm.role}
                        onChange={(e) => setBgvForm({ ...bgvForm, role: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date of Joining</label>
                      <input
                        type="date"
                        value={bgvForm.doj}
                        onChange={(e) => setBgvForm({ ...bgvForm, doj: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">BGV Status</label>
                      <select
                        value={bgvForm.bgv_status}
                        onChange={(e) => setBgvForm({ ...bgvForm, bgv_status: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="yes">Verified</option>
                        <option value="no">Not Verified</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Agency</label>
                      <input
                        type="text"
                        value={bgvForm.bgv_agency}
                        onChange={(e) => setBgvForm({ ...bgvForm, bgv_agency: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Background Verification Status</label>
                      <select
                        value={bgvForm.bgv_status}
                        onChange={(e) => setBgvForm({ ...bgvForm, bgv_status: e.target.value })}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="yes">Verified</option>
                        <option value="no">Not Verified</option>
                      </select>
                    </div>

                    {bgvForm.bgv_status === "yes" && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Verification Type</label>
                          <select
                            value={bgvForm.verification_type || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setBgvForm({
                                ...bgvForm,
                                verification_type: value,
                              });
                              if (value === "agency") {
                                fetchAgencies();
                              }
                            }}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">-- Select Type --</option>
                            <option value="internal">Internal</option>
                            <option value="agency">Agency</option>
                          </select>
                        </div>

                        {bgvForm.verification_type === "agency" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Verification Agency</label>
                            <select
                              value={bgvForm.bgv_agency || ""}
                              onChange={(e) => setBgvForm({ ...bgvForm, bgv_agency: e.target.value })}
                              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">-- Select Agency --</option>
                              {agencies.length > 0 ? (
                                agencies.map((agency) => (
                                  <option key={agency.id} value={agency.name}>
                                    {agency.name}
                                  </option>
                                ))
                              ) : (
                                <option disabled>Loading agencies...</option>
                              )}
                            </select>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBgvModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBgvRecord}
                    className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                  >
                    {modalMode === "create" ? "Create" : "Update"}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <UploadFileModal
        showUploadFile={showUploadFile}
        setShowUploadFile={setShowUploadFile}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        handleUploadFile={handleUploadFile}
        title="Upload Verification Document"
        subtitle="Upload documents for background verification completion"
        acceptedFileTypes=".pdf,.doc,.docx,.png,.jpg,.jpeg"
        maxFileSize={2}
        fileTypeLabel={["PDF", "DOC", "DOCX", "PNG", "JPG", "JPEG"]}
      />
    </div>
  );
};

export default BGVTab;
